import React, { useState } from 'react';
import {
  Shield, Scale, FileText, Cookie, Users, Copyright, Sparkles, UserCheck,
  ChevronDown, ChevronUp, Mail, Clock, ExternalLink
} from 'lucide-react';

const LAST_UPDATED = 'March 27, 2026';
const CONTACT_EMAIL = 'privacy@starnightholdings.uk';

const COLOR_MAP = {
  cyan: { bg: 'bg-cyan-500/15', border: 'border-cyan-500/25', text: 'text-cyan-300', dot: 'bg-cyan-400', glow: 'shadow-cyan-500/10' },
  violet: { bg: 'bg-violet-500/15', border: 'border-violet-500/25', text: 'text-violet-300', dot: 'bg-violet-400', glow: 'shadow-violet-500/10' },
  amber: { bg: 'bg-amber-500/15', border: 'border-amber-500/25', text: 'text-amber-300', dot: 'bg-amber-400', glow: 'shadow-amber-500/10' },
  emerald: { bg: 'bg-emerald-500/15', border: 'border-emerald-500/25', text: 'text-emerald-300', dot: 'bg-emerald-400', glow: 'shadow-emerald-500/10' },
  rose: { bg: 'bg-rose-500/15', border: 'border-rose-500/25', text: 'text-rose-300', dot: 'bg-rose-400', glow: 'shadow-rose-500/10' },
  fuchsia: { bg: 'bg-fuchsia-500/15', border: 'border-fuchsia-500/25', text: 'text-fuchsia-300', dot: 'bg-fuchsia-400', glow: 'shadow-fuchsia-500/10' },
  sky: { bg: 'bg-sky-500/15', border: 'border-sky-500/25', text: 'text-sky-300', dot: 'bg-sky-400', glow: 'shadow-sky-500/10' },
};

const POLICIES = [
  {
    id: 'privacy',
    icon: Shield,
    color: 'cyan',
    title: 'Privacy Policy',
    tagline: 'How we collect, use, and safeguard your personal information.',
    intro: 'Starnight Holdings is committed to protecting your privacy under the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. This policy explains what data we hold, why we hold it, and how you stay in control.',
    sections: [
      { subtitle: 'Information We Collect', text: 'Account credentials (username, hashed password, email), optional profile details (name, phone, DOB, location, bio), and interaction data such as chat transcripts with Nova, page usage, and device metadata.' },
      { subtitle: 'How We Use It', text: 'To authenticate you, personalise your experience, deliver customer support, keep the platform secure, and improve our services through anonymised analytics.' },
      { subtitle: 'Security', text: 'All data in transit is encrypted via TLS. Passwords are hashed using bcrypt and are never visible to staff. Access is role-based, monitored, and audited.' },
      { subtitle: 'Retention', text: 'We keep account data while your account is active. Chat transcripts are retained up to 12 months for quality assurance. On deletion, personal data is purged within 30 days.' },
      { subtitle: 'Data Sharing', text: 'We never sell your personal data. Limited sharing occurs only with trusted infrastructure providers (hosting, AI compute) under strict data processing agreements.' },
      { subtitle: 'Breach Notification', text: 'In the unlikely event of a data breach, affected users will be notified within 72 hours as required by UK GDPR.' },
    ]
  },
  {
    id: 'terms',
    icon: Scale,
    color: 'violet',
    title: 'Terms of Service',
    tagline: 'The agreement that governs your use of Starnight services.',
    intro: 'By creating an account or using our platform you agree to these Terms. They form a binding contract between you and Starnight Holdings Ltd. If you do not accept them, please stop using our services.',
    sections: [
      { subtitle: 'Eligibility', text: 'You must be at least 13 years old (or the digital consent age in your jurisdiction) to create an account. Staff or partner accounts may require verification of legal age and residency.' },
      { subtitle: 'Your Account', text: 'You are responsible for all activity under your account and for keeping your credentials secure. Sharing accounts or bypassing security measures is prohibited.' },
      { subtitle: 'Acceptable Use', text: 'You agree not to abuse, harass, defraud, spam, or attempt to reverse-engineer, disrupt, or gain unauthorised access to our systems.' },
      { subtitle: 'Content Ownership', text: 'You retain ownership of the content you post. By posting, you grant Starnight a non-exclusive, worldwide licence to host, display, and distribute it as required to operate the service.' },
      { subtitle: 'Termination', text: 'We may suspend or terminate accounts that violate these Terms, applicable law, or that pose a security risk. You may close your account at any time from Data & Privacy.' },
      { subtitle: 'Liability', text: 'Our services are provided "as is" without warranties beyond those required by law. To the maximum extent permitted, Starnight is not liable for indirect or consequential losses.' },
      { subtitle: 'Governing Law', text: 'These Terms are governed by the laws of England and Wales. Disputes will be resolved in the courts of England and Wales.' },
    ]
  },
  {
    id: 'cookies',
    icon: Cookie,
    color: 'amber',
    title: 'Cookie Policy',
    tagline: 'Small files we use to keep the platform running smoothly.',
    intro: 'We use cookies and similar technologies (like localStorage) to keep you signed in, remember your preferences, and understand how the platform is being used. You control what is optional.',
    sections: [
      { subtitle: 'Strictly Necessary', text: 'Session tokens, CSRF protection, and security cookies required to sign in and use the platform. These cannot be disabled.' },
      { subtitle: 'Preferences', text: 'Remember your theme choice, notification settings, and layout preferences. Deleting them will reset your account view.' },
      { subtitle: 'Analytics', text: 'Anonymised usage counters help us understand which features are loved and which need work. No personally identifying information is sent to third parties.' },
      { subtitle: 'No Advertising', text: 'We do not use advertising cookies, retargeting pixels, or share data with ad networks.' },
      { subtitle: 'Managing Cookies', text: 'You can clear cookies from your browser settings at any time. Doing so will log you out and reset preferences but will not delete your account.' },
    ]
  },
  {
    id: 'community',
    icon: Sparkles,
    color: 'fuchsia',
    title: 'Community Guidelines',
    tagline: 'How members treat each other inside the Lounge and Live Chat.',
    intro: 'The Lounge is a members-only space. To keep it kind, creative, and safe, everyone follows the same guidelines — staff, executives, and community members alike.',
    sections: [
      { subtitle: 'Be Respectful', text: 'No harassment, bullying, hate speech, threats, or targeted attacks. Disagree with the idea, never the person.' },
      { subtitle: 'Keep It Safe For Work', text: 'No sexually explicit content, gore, self-harm content, or shock media. Media uploads are moderated automatically and by staff.' },
      { subtitle: 'No Doxxing or Impersonation', text: 'Do not share private information about others without consent. Do not impersonate staff, executives, or other members.' },
      { subtitle: 'No Spam or Scams', text: 'No pyramid schemes, cryptocurrency shilling, unauthorised advertising, or repetitive promotional posts.' },
      { subtitle: 'Report, Don\'t Retaliate', text: 'Use the report action on any post or comment that breaks the rules. Our moderators respond within 24 hours.' },
      { subtitle: 'Consequences', text: 'Violations may result in content removal, a temporary mute, permanent ban from the Lounge, or full account termination for severe breaches.' },
    ]
  },
  {
    id: 'dmca',
    icon: Copyright,
    color: 'rose',
    title: 'DMCA & Copyright',
    tagline: 'How to report content that infringes your rights.',
    intro: 'Starnight respects intellectual property. If you believe content on our platform infringes your copyright, submit a takedown notice and we will act promptly.',
    sections: [
      { subtitle: 'What to Include', text: 'A description of the copyrighted work, the URL of the infringing content, your contact details, a good-faith statement, and a signature (digital is fine).' },
      { subtitle: 'Where to Send', text: `Email our designated agent at ${CONTACT_EMAIL} with the subject line "DMCA Takedown Notice".` },
      { subtitle: 'Counter Notices', text: 'If your content was removed and you believe it was in error, you may submit a counter notice with the same contact details.' },
      { subtitle: 'Repeat Infringers', text: 'Accounts that repeatedly infringe others\' rights will be terminated in accordance with applicable law.' },
      { subtitle: 'False Claims', text: 'Filing a false or bad-faith DMCA notice may result in liability under Section 512(f) of the DMCA and equivalent UK law.' },
    ]
  },
  {
    id: 'acceptable-use',
    icon: FileText,
    color: 'sky',
    title: 'Acceptable Use Policy',
    tagline: 'What is allowed — and what will get your account paused.',
    intro: 'This policy applies to every corner of the platform: profile, messaging, chat, Lounge, and any API access.',
    sections: [
      { subtitle: 'Prohibited Content', text: 'Illegal content, CSAM, terrorist content, targeted harassment, malware, phishing, credential stuffing, or content designed to defraud.' },
      { subtitle: 'Prohibited Behaviour', text: 'Automated scraping, brute-forcing endpoints, evading bans, bulk account creation, or attempting to reach admin surfaces.' },
      { subtitle: 'Security Research', text: 'Responsible disclosure is welcome. Contact us before probing production. We do not authorise unauthorised penetration testing.' },
      { subtitle: 'API Usage', text: 'Respect rate limits. Do not attempt to reverse-engineer authentication flows or bypass our zero-trust security perimeter.' },
      { subtitle: 'Enforcement', text: 'Violations may result in temporary suspension, permanent ban, referral to law enforcement, or civil action where appropriate.' },
    ]
  },
  {
    id: 'data-rights',
    icon: UserCheck,
    color: 'emerald',
    title: 'Your Data Rights (GDPR)',
    tagline: 'Rights you can exercise directly from this page.',
    intro: 'Under UK GDPR you have specific rights over your personal data. Most can be exercised instantly from your Profile Hub — no email required.',
    sections: [
      { subtitle: 'Right of Access', text: 'View all your data in the Overview and Profile tabs, or use "Export my data" above for a full JSON snapshot.' },
      { subtitle: 'Right to Rectification', text: 'Update anything at any time via the Profile tab. Changes take effect immediately.' },
      { subtitle: 'Right to Erasure', text: 'Delete your account and all associated data from the Danger Zone below. Deletion is permanent and irreversible.' },
      { subtitle: 'Right to Portability', text: 'Export your data as machine-readable JSON — perfect for moving to another service.' },
      { subtitle: 'Right to Object', text: 'Opt out of marketing communications in the Preferences tab. Essential service emails cannot be disabled while you have an account.' },
      { subtitle: 'Right to Complain', text: `If you feel we haven\'t handled your data properly, contact our DPO at ${CONTACT_EMAIL} or lodge a complaint with the UK Information Commissioner\'s Office (ICO).` },
    ]
  },
];

const PoliciesPanel = () => {
  const [expanded, setExpanded] = useState(null);

  return (
    <div className="space-y-3" data-testid="policies-panel">
      <div className="flex items-center justify-between pb-1">
        <div className="flex items-center gap-2">
          <Scale className="w-4 h-4 text-white/40" />
          <h3 className="text-white text-sm font-medium tracking-wide">Legal & Policies</h3>
        </div>
        <div className="flex items-center gap-1.5 text-white/30 text-[11px]">
          <Clock className="w-3 h-3" />
          <span>Updated {LAST_UPDATED}</span>
        </div>
      </div>
      <p className="text-white/40 text-xs -mt-1 mb-2">
        Everything you sign when you use Starnight — one click away.
      </p>

      {POLICIES.map((p) => {
        const isOpen = expanded === p.id;
        const c = COLOR_MAP[p.color];
        const Icon = p.icon;
        return (
          <div
            key={p.id}
            data-testid={`policy-card-${p.id}`}
            className={`rounded-2xl overflow-hidden transition-all border ${
              isOpen ? `${c.border} bg-white/[0.02] shadow-lg ${c.glow}` : 'border-white/[0.06] bg-white/[0.015] hover:border-white/[0.12]'
            }`}
          >
            <button
              onClick={() => setExpanded(isOpen ? null : p.id)}
              data-testid={`policy-toggle-${p.id}`}
              className="w-full px-5 py-4 flex items-center gap-4 text-left group"
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border ${c.bg} ${c.border}`}>
                <Icon className={`w-4.5 h-4.5 ${c.text}`} strokeWidth={1.75} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-white text-sm font-medium">{p.title}</h4>
                <p className="text-white/40 text-xs mt-0.5 truncate">{p.tagline}</p>
              </div>
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${isOpen ? c.bg : 'bg-white/[0.04]'}`}>
                {isOpen ? <ChevronUp className={`w-3.5 h-3.5 ${c.text}`} /> : <ChevronDown className="w-3.5 h-3.5 text-white/40" />}
              </div>
            </button>

            {isOpen && (
              <div className="px-5 pb-5 pt-0 space-y-3" style={{ animation: 'policyFadeIn 0.25s ease-out' }}>
                <div className="h-px bg-white/[0.06]" />
                <p className="text-white/60 text-xs leading-relaxed italic">{p.intro}</p>
                <div className="space-y-2.5 pt-1">
                  {p.sections.map((s, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className={`w-1.5 h-1.5 ${c.dot} rounded-full mt-1.5 flex-shrink-0`} />
                      <div className="flex-1">
                        <span className="text-white text-xs font-semibold">{s.subtitle}</span>
                        <span className="text-white/50 text-xs"> — {s.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div className="mt-4 rounded-2xl border border-white/[0.06] bg-gradient-to-br from-white/[0.02] to-transparent px-5 py-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center flex-shrink-0">
          <Mail className="w-4 h-4 text-[#00E5FF]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-xs font-medium">Questions or requests?</p>
          <p className="text-white/40 text-[11px] mt-0.5">Reach our Data Protection Officer for anything policy-related.</p>
        </div>
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          data-testid="policies-contact-dpo"
          className="text-[#00E5FF] hover:text-white text-xs font-medium inline-flex items-center gap-1.5 transition-colors"
        >
          Contact <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      <style>{`
        @keyframes policyFadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default PoliciesPanel;
